/**
 * FissalBubble.jsx — global floating chat bubble (#254).
 *
 * Fissal's lightweight, persistent chat drawer. Always accessible from
 * any dashboard — click the clockwork-Khajiit face to open a compact
 * pocket chat without leaving the current layout.
 *
 * Voice rules (fissal-voice-curator.agent.md):
 *   - Jone tone: short, bright, fast — quick quips only
 *   - Jode / deep archive: redirect to the full FissalAI widget
 *   - Whimsy budget: one short aside per message max
 *
 * CSP: all handlers attached via Solid event props. No inline attrs.
 */
import { createSignal, Show, For, onMount, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import wm from '../lib/wmStore.js';
import { cleanFissalOutput } from '../lib/fissalCleanup.js';
import { renderFissalMarkdown } from '../lib/renderMarkdown.jsx';
import {
  fissalHttpFallback,
  fissalNetworkFallback,
  fissalEmptyFallback,
  isEmptyFissalReply,
} from '../lib/fissalFallback.js';
import './FissalBubble.css';

const WELCOME_CUE =
  '*gears whirr* Jone pings bright — quick question? Or open the full chat for deep dives.';

export default function FissalBubble() {
  const [open,     setOpen]     = createSignal(false);
  const [input,    setInput]    = createSignal('');
  const [messages, setMessages] = createSignal([]);
  const [busy,     setBusy]     = createSignal(false);
  const [sessionId, setSessionId] = createSignal(null);

  let msgEnd;
  let inputRef;

  function toggle() {
    const next = !open();
    setOpen(next);
    if (next) {
      if (messages().length === 0) {
        setMessages([{ role: 'fissal', text: WELCOME_CUE }]);
      }
      setTimeout(() => inputRef?.focus(), 60);
    }
  }

  function close() { setOpen(false); }

  function openFull() {
    close();
    wm.addWidget('fissal-open-webui');
  }

  async function send() {
    const q = input().trim();
    if (!q || busy()) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const r = await fetch('/api/fissal/analyze', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: q, contextType: 'general', weeksAgo: 0, session_id: sessionId() }),
      });
      const j = await r.json().catch(() => ({}));
      let text;
      if (!r.ok) {
        text = fissalHttpFallback(r.status, j);
      } else {
        if (j.session_id) setSessionId(j.session_id);
        const cleaned = cleanFissalOutput(j.analysis || j.response || j.text || j.reply || '');
        text = isEmptyFissalReply(cleaned) ? fissalEmptyFallback() : cleaned;
      }
      setMessages(m => [...m, { role: 'fissal', text }]);
    } catch {
      setMessages(m => [...m, { role: 'fissal', text: fissalNetworkFallback() }]);
    } finally {
      setBusy(false);
      setTimeout(() => msgEnd?.scrollIntoView({ behavior: 'smooth' }), 30);
    }
  }

  function onKeyDown(e) {
    if (open() && e.key === 'Escape') { e.preventDefault(); close(); }
  }

  function onInputKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  onMount(()   => document.addEventListener('keydown', onKeyDown));
  onCleanup(() => document.removeEventListener('keydown', onKeyDown));

  return (
    <Show when={!!wm.state.viewer}>
      <Portal>
        <div class="v2-fbubble">

          {/* ── Compact chat drawer ── */}
          <Show when={open()}>
            <div class="v2-fbubble__drawer"
              role="dialog"
              aria-label="Fissal quick chat"
              aria-modal="false"
            >
              <div class="v2-fbubble__hdr">
                <span class="v2-fbubble__title">🐱 Fissal</span>
                <button class="v2-fbubble__hdr-btn"
                  title="Open full Fissal chat"
                  aria-label="Open full Fissal chat"
                  onClick={openFull}>⊞</button>
                <button class="v2-fbubble__hdr-btn v2-fbubble__hdr-btn--close"
                  title="Minimise"
                  aria-label="Minimise chat"
                  onClick={close}>⊟</button>
              </div>

              <div class="v2-fbubble__msgs">
                <For each={messages()}>{(m) => (
                  <div class={`v2-fbubble__msg v2-fbubble__msg--${m.role}`}>
                    <span class="v2-fbubble__who" aria-hidden="true">
                      {m.role === 'fissal' ? '🐱' : '▸'}
                    </span>
                    <div class="v2-fbubble__body">
                      {m.role === 'fissal'
                        ? renderFissalMarkdown(m.text)
                        : m.text}
                    </div>
                  </div>
                )}</For>
                <Show when={busy()}>
                  <div class="v2-fbubble__thinking">Calibrating…</div>
                </Show>
                <div ref={msgEnd} />
              </div>

              <form class="v2-fbubble__form"
                onSubmit={(e) => { e.preventDefault(); send(); }}
              >
                <input
                  ref={inputRef}
                  class="v2-input v2-fbubble__input"
                  type="text"
                  value={input()}
                  onInput={(e)   => setInput(e.currentTarget.value)}
                  onKeyDown={onInputKey}
                  placeholder="Quick question for Fissal…"
                  disabled={busy()}
                  autocomplete="off"
                  aria-label="Message Fissal"
                />
                <button class="v2-btn-sm" type="submit"
                  disabled={busy() || !input().trim()}
                  aria-label="Send message"
                >▸</button>
              </form>
            </div>
          </Show>

          {/* ── Bubble trigger button ── */}
          <button
            class="v2-fbubble__btn"
            classList={{ 'is-busy': busy() }}
            type="button"
            title="Ask Fissal"
            aria-label="Open Fissal quick chat"
            aria-expanded={open()}
            aria-busy={busy()}
            data-hint={'Ask Fissal \u2014 the clockwork-Khajiit advisor.\nQuick questions here, or open the full chat for deep dives.'}
            data-hint-level="critical"
            onClick={toggle}
          >
            <span class="v2-fbubble__btn-face" aria-hidden="true">🐱</span>
            <Show when={busy()}>
              <span class="v2-fbubble__gear-ring" aria-hidden="true"></span>
            </Show>
          </button>

        </div>
      </Portal>
    </Show>
  );
}
